import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from './base';
import { GenericAPIResponse, Project, ProvisionalTaxDate } from '../definitions';

@Injectable()
export class DatesService extends BaseService {
  private urlMap = [
    {
      fn: 'getBalanceDates',
      dashboard: 'date/balance/dates',
      admin: 'dates/balance/dates',
    },
    {
      fn: 'getProvisionalTaxDates',
      dashboard: 'date/provisional-by-tax-type',
      admin: 'dates/provisional-by-tax-type',
    },
    {
      fn: 'getPaymentDates',
      dashboard: 'date/payment-dates',
      admin: 'dates/payment-dates',
    },
    {
      fn: 'getMaturityDates',
      url: 'tax/finance/maturitydates',
    },
    {
      fn: 'getNextPurchaseCalculationDate',
      dashboard: 'date/next-purchase-calculation',
      admin: 'dates/next-purchase-calculation',
    },
  ];

  constructor(private http: HttpClient, @Inject('project') public project: Project) {
    super(project);
  }

  private getUrl(fn: string): string {
    return this.getApiEndpoint(fn, this.urlMap);
  }

  public getProvisionalTaxDates(data: {
    balanceDate: string;
    incomeYear: number | string;
    taxType?: string | number;
    numPayments?: string | number;
    provTaxBasis?: number;
    reverseOrder?: boolean;
    accountId?: number;
    terminalTaxDate?: string;
  }) {
    return this.http.post<ProvisionalTaxDate[]>(
      this.getUrl('getProvisionalTaxDates'),
      data,
      this.requestOptions,
    );
  }

  public getPaymentDates(
    isAuditTax: boolean,
    args: { reassessmentDate: string; terminalTaxDate: string },
    accountId?: number,
    incomeYear?: number,
  ) {
    let params = new HttpParams();

    if (isAuditTax) {
      params = params.append('auditTax', '1');
      params = params.append('reassessmentDate', args['reassessmentDate']);
    } else {
      params = params.append('auditTax', '0');
      params = params.append('terminalTaxDate', args['terminalTaxDate']);
    }

    if (accountId) params = params.append('accountId', accountId);
    if (incomeYear) params = params.append('incomeYear', incomeYear);

    return this.http.get<GenericAPIResponse<PaymentDates>>(this.getUrl('getPaymentDates'), {
      params,
    });
  }

  public getMaturityDates(
    terminalTaxDate: string,
    balanceDate: string,
    incomeYear: string,
    omitFirstOption: boolean = false,
    accountId: number,
    isAuditTax: boolean,
    reassessmentDate: string,
  ) {
    let params = new HttpParams();
    params = params.append('terminalTaxDate', terminalTaxDate);
    params = params.append('balanceDate', balanceDate);
    params = params.append('incomeYear', incomeYear);
    params = params.append('omitFirstOption', `${omitFirstOption}`);
    params = params.append('accountId', `${accountId}`);
    params = params.append('isAuditTax', isAuditTax);
    params = params.append('reassessmentDate', reassessmentDate);

    return this.http.get<GenericAPIResponse<GetMaturityDatesResponse>>(
      this.getUrl('getMaturityDates'),
      { params },
    );
  }

  public getBalanceDates() {
    return this.http.get<BalanceDatesAPIResponse[]>(this.getUrl('getBalanceDates'));
  }

  public getTaxPeriods(taxTypeId: number) {
    const apiEndpoint = `${this.baseUrl}/dates/tax-period-dates`;

    let params = new HttpParams();
    params = params.append('taxTypeId', taxTypeId);

    return this.http.get<PeriodDate[]>(apiEndpoint, { params });
  }

  public getAllTerminalTaxDates() {
    const apiEndpoint = `${this.baseUrl}/dates/terminal`;

    return this.http.get<string[]>(apiEndpoint);
  }

  public getNextPurchaseCalculationDate(allowToday = false): Observable<PurchaseCalculationDate> {
    const params = new HttpParams().set('allowToday', allowToday);
    return this.http.get<PurchaseCalculationDate>(this.getUrl('getNextPurchaseCalculationDate'), {
      params,
    });
  }
}

export type BalanceDatesAPIResponse = {
  balanceDate: string;
  terminalTaxDate: string;
  terminalTaxDateWithExtension: string;
  terminalTaxDateYear: number;
  terminalTaxDateWithExtensionYear: number;
  dateOrder: number;
  balanceDateYear?: number;
  balanceDateFormatted: string;
  terminalTaxDateFormatted: string;
  terminalTaxDateWithExtensionFormatted: string;
};

export type GetMaturityDatesResponse = {
  defaultMaturityDate: number;
  formattedDates: {
    maturityDate: string;
    lenderMaturityDate: string;
  }[];
  rawDates: {
    lenderMaturityDate: string;
    maturityDate: string;
  }[];
};

export interface PeriodDate {
  dateId: number;
  dueDate: string;
  periodDate: string;
  taxTypeId: number;
}

export interface PurchaseCalculationDate {
  calculationDate: DateStringStandard;
  rolloverDate: DateStringStandard;
  paymentDate: DateStringStandard;
  isCustom?: boolean;
}

export interface PaymentDates {
  dates: PurchaseCalculationDate[];
  upperLimit: string;
}
